import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Check, Save, X } from 'lucide-react';
import styled from 'styled-components';
import { Button, Empty, HintNote, Message } from '../admin/ui';
import * as Api from './PersonAdminApi';
import { errorText, originLabel } from './personAdminModel';
import { Sources, formatMoment, valueText } from './personAdminParts';

// The change sets that wait for the admin's decision on one person (PersonChangeSetController): what
// research or an import proposed, each field with the current value next to the proposed one, its
// confidence and sources, and a toggle per field - Accept or Reject, pressing the chosen one again
// takes the choice back. Nothing is sent until the set's one save button, which names what it does
// ("Save 3 decisions"); then the owner reads the person again (`onChanged`) and the sets show what
// became of each field. A set that was just decided stays on screen with those results even when
// saving finished it. What saving does depends on the account: without one every accepted field is
// written to the profile at once; with one the accepted values are not applied yet, they travel with
// the person's next invitation and the person decides there (PersonService.decideAdmin, D9).
//
// The layout follows the width of the card, not of the window (a container query): below 40rem each
// field is a card of its own, above it the fields line up as columns. Used by the person detail
// screen and the program screen's person panel.

const WIDE = '40rem';

const Wrap = styled.div`container-type: inline-size;`;
const Rows = styled.ul`list-style: none; margin: .6rem 0 0; padding: 0; display: flex; flex-direction: column; gap: .5rem;`;
const Row = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: .4rem;
  padding: .65rem .75rem;
  border: 1px solid #e2e8f0;
  border-left: 4px solid ${(p) => (p.$choice === 'ACCEPT' ? '#16a34a' : p.$choice === 'REJECT' ? '#dc2626' : '#cbd5e1')};
  background: ${(p) => (p.$choice === 'ACCEPT' ? '#f0fdf4' : p.$choice === 'REJECT' ? '#fef2f2' : '#fff')};
  font-size: .83rem;
  @container (min-width: ${WIDE}) {
    grid-template-columns: 7rem minmax(0, 1fr) minmax(0, 1.4fr) 12rem;
    gap: .75rem;
  }
`;
const HeadRow = styled.li`
  display: none;
  @container (min-width: ${WIDE}) {
    display: grid;
    grid-template-columns: 7rem minmax(0, 1fr) minmax(0, 1.4fr) 12rem;
    gap: .75rem;
    padding: 0 .75rem 0 calc(.75rem + 4px);
    font-size: .72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #475569;
  }
`;
const CellLabel = styled.div`
  font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #64748b;
  @container (min-width: ${WIDE}) { display: none; }
`;
const FieldName = styled.div`font-weight: 700; color: #0f172a;`;
const Cell = styled.div`min-width: 0; overflow-wrap: anywhere;`;
const Toggles = styled.div`display: flex; gap: .4rem;`;
// Not chosen: outlined in its colour. Chosen: filled, so it can be told from across the room.
const Toggle = styled.button`
  display: inline-flex; align-items: center; gap: .3rem; padding: .35rem .65rem; font: inherit; font-weight: 700; cursor: pointer;
  border: 2px solid ${(p) => (p.$reject ? '#dc2626' : '#16a34a')};
  background: ${(p) => (p.$pressed ? (p.$reject ? '#dc2626' : '#16a34a') : '#fff')};
  color: ${(p) => (p.$pressed ? '#fff' : (p.$reject ? '#b91c1c' : '#15803d'))};
  &:hover:not(:disabled) { background: ${(p) => (p.$pressed ? (p.$reject ? '#b91c1c' : '#15803d') : (p.$reject ? '#fef2f2' : '#f0fdf4'))}; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Outcome = styled.div`margin-top: .3rem; font-size: .75rem; font-weight: 700; color: ${(p) => (p.$choice === 'ACCEPT' ? '#15803d' : p.$choice === 'REJECT' ? '#b91c1c' : '#64748b')};`;
const Bulk = styled.button`border: 0; background: none; padding: 0; font: inherit; font-size: .78rem; font-weight: 700; color: #0369a1; cursor: pointer; text-decoration: underline; &:disabled { opacity: .5; cursor: not-allowed; }`;

// A field can be decided when its set is still OPEN and the backend shows no admin decision yet; a
// field the admin already decided (an ADMIN set on an account holder) is waiting for the invitation.
function decorate(changeSets) {
  return (changeSets || []).map((set) => ({
    ...set,
    fields: set.fields.map((change) => ({ ...change, canBeDecided: set.status === 'OPEN' && change.adminDecision == null })),
  }));
}

const isBlank = (value) => value == null || value === '' || (Array.isArray(value) && value.length === 0);

// Blank current fields are pre-selected ACCEPT, as before (design §4.2.3 step 2).
function initialDecisions(sets) {
  const out = {};
  for (const set of sets) {
    const chosen = {};
    for (const change of set.fields) {
      if (change.canBeDecided && isBlank(change.currentValue)) chosen[change.field] = 'ACCEPT';
    }
    if (Object.keys(chosen).length) out[set.id] = chosen;
  }
  return out;
}

const StateLabel = ({ state, waiting }) => {
  const key = waiting && state === 'ACCEPTED' ? 'WAITING' : state;
  return (
    <span style={{
      fontSize: '.72rem', fontWeight: 700, padding: '.12rem .45rem',
      background: state === 'PENDING' ? '#f1f5f9' : state === 'REJECTED' ? '#fee2e2' : key === 'WAITING' ? '#fef3c7' : '#dcfce7',
      color: state === 'PENDING' ? '#475569' : state === 'REJECTED' ? '#991b1b' : key === 'WAITING' ? '#92400e' : '#166534',
    }}>
      <FormattedMessage id={`personAdmin.changeSet.state.${key}`} defaultMessage={key} />
    </span>
  );
};

const ChangeSetsCard = ({ personId, changeSets, hasAccount = false, onChanged }) => {
  const intl = useIntl();
  const t = useCallback((key, def, values) => intl.formatMessage({ id: key, defaultMessage: def }, values), [intl]);
  const sets = useMemo(() => decorate(changeSets), [changeSets]);
  const [decisions, setDecisions] = useState(() => initialDecisions(sets));
  const [savingSet, setSavingSet] = useState(null);
  const [message, setMessage] = useState(null);
  // Sets decided here stay listed after the save, even when it finished them.
  const [kept, setKept] = useState(() => new Set());

  // A fresh read of the person starts the choices again from what the sets say now (state adjusted
  // while rendering, so the first render of a set never races an effect).
  const [seen, setSeen] = useState(sets);
  if (seen !== sets) {
    setSeen(sets);
    setDecisions(initialDecisions(sets));
  }

  const shown = useMemo(() => sets.filter((set) => set.status === 'OPEN' || set.status === 'SENT' || kept.has(set.id)), [sets, kept]);
  const pendingCount = useMemo(() => shown.reduce((sum, set) => sum + set.fields.filter((f) => f.canBeDecided).length, 0), [shown]);

  const choiceOf = (set, field) => decisions[set.id]?.[field] || null;
  const chosenOf = (set) => Object.fromEntries(set.fields
    .filter((change) => change.canBeDecided && choiceOf(set, change.field))
    .map((change) => [change.field, choiceOf(set, change.field)]));

  // Pressing the chosen button again takes the choice back.
  const decide = (setId, field, decision) => setDecisions((current) => {
    const own = { ...(current[setId] || {}) };
    if (own[field] === decision) delete own[field]; else own[field] = decision;
    return { ...current, [setId]: own };
  });
  const decideAll = (set, decision) => setDecisions((current) => ({
    ...current,
    [set.id]: Object.fromEntries(set.fields.filter((change) => change.canBeDecided).map((change) => [change.field, decision])),
  }));

  const save = async (set) => {
    const chosen = chosenOf(set);
    const values = Object.values(chosen);
    if (values.length === 0) return;
    setSavingSet(set.id);
    setMessage(null);
    try {
      await Api.decideChangeSet(personId, set.id, chosen);
      setKept((current) => new Set(current).add(set.id));
      await onChanged?.();
      const counts = { accepted: values.filter((v) => v === 'ACCEPT').length, rejected: values.filter((v) => v === 'REJECT').length };
      setMessage({ error: false, text: hasAccount
        ? t('personAdmin.changeSet.savedWaiting', 'Saved: {accepted} accepted, {rejected} rejected. Accepted values wait for the next invitation.', counts)
        : t('personAdmin.changeSet.savedApplied', 'Saved: {accepted} accepted, {rejected} rejected. Accepted values are on the profile now.', counts) });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.changeSet.failed', 'Could not save the decisions.')) });
    } finally {
      setSavingSet(null);
    }
  };

  const close = async (set) => {
    setSavingSet(set.id);
    setMessage(null);
    try {
      await Api.closeChangeSet(personId, set.id);
      await onChanged?.();
      setMessage({ error: false, text: t('personAdmin.changeSet.closed', 'The change set was closed without applying the rest.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.changeSet.closeFailed', 'Could not close the change set.')) });
    } finally {
      setSavingSet(null);
    }
  };

  return (
    <Wrap>
      <h2 style={{ fontSize: '1rem', margin: '0 0 .4rem' }}>
        <FormattedMessage id="personAdmin.changeSet.title" defaultMessage="Proposed changes" />
        {pendingCount > 0 && <> · {t('personAdmin.changeSet.pending', '{count} to decide', { count: pendingCount })}</>}
      </h2>
      {pendingCount > 0 && (
        <p style={{ margin: '0 0 .5rem', fontSize: '.8rem', color: '#475569', lineHeight: 1.5 }}>
          {hasAccount
            ? <FormattedMessage id="personAdmin.changeSet.explainAccount"
              defaultMessage="This person has an account: accepted values are not applied yet. They are sent with the next invitation and the person confirms them." />
            : <FormattedMessage id="personAdmin.changeSet.explainNoAccount"
              defaultMessage="This person has no account: accepted values are applied to the profile as soon as you save." />}
        </p>
      )}
      {message && <Message error={message.error} role={message.error ? 'alert' : 'status'}>{message.text}</Message>}
      {shown.length === 0 ? (
        <Empty style={{ padding: '1rem 0' }}><FormattedMessage id="personAdmin.changeSet.empty" defaultMessage="Nothing waits for a decision." /></Empty>
      ) : shown.map((set) => {
        const decidable = set.fields.filter((change) => change.canBeDecided);
        const count = Object.keys(chosenOf(set)).length;
        const busy = savingSet === set.id;
        return (
          <section key={set.id} data-testid="change-set" style={{ borderTop: '1px solid #eef2f7', padding: '.8rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <h3 style={{ margin: 0, fontSize: '.9rem' }}>
                <FormattedMessage id="personAdmin.changeSet.set" defaultMessage="{origin} change set" values={{ origin: originLabel(t, set.origin) }} />
                {' · '}
                <span style={{ fontSize: '.76rem', fontWeight: 400, color: '#64748b' }}>{formatMoment(intl, set.createdAt)}</span>
              </h3>
              {set.status === 'SENT' && (
                <span style={{ fontSize: '.75rem', color: '#92400e', background: '#fef3c7', padding: '.15rem .5rem' }}>
                  <FormattedMessage id="personAdmin.changeSet.sent" defaultMessage="Sent with an invitation — waiting for the person" />
                </span>
              )}
              {(set.status === 'APPLIED' || set.status === 'CLOSED') && (
                <span style={{ fontSize: '.75rem', color: '#166534', background: '#dcfce7', padding: '.15rem .5rem' }}>
                  <FormattedMessage id={`personAdmin.changeSet.status.${set.status}`} defaultMessage={set.status} />
                </span>
              )}
            </div>

            {decidable.length > 1 && (
              <div style={{ display: 'flex', gap: '.9rem', margin: '.5rem 0 0' }}>
                <Bulk type="button" disabled={busy} onClick={() => decideAll(set, 'ACCEPT')}>
                  <FormattedMessage id="personAdmin.changeSet.acceptAll" defaultMessage="Accept all" />
                </Bulk>
                <Bulk type="button" disabled={busy} onClick={() => decideAll(set, 'REJECT')}>
                  <FormattedMessage id="personAdmin.changeSet.rejectAll" defaultMessage="Reject all" />
                </Bulk>
              </div>
            )}

            <Rows>
              <HeadRow aria-hidden="true">
                <span><FormattedMessage id="personAdmin.changeSet.field" defaultMessage="Field" /></span>
                <span><FormattedMessage id="personAdmin.changeSet.current" defaultMessage="Current" /></span>
                <span><FormattedMessage id="personAdmin.changeSet.proposed" defaultMessage="Proposed" /></span>
                <span><FormattedMessage id="personAdmin.changeSet.decision" defaultMessage="Decision" /></span>
              </HeadRow>
              {set.fields.map((change) => {
                const choice = change.canBeDecided ? choiceOf(set, change.field) : null;
                const label = t(`personAdmin.field.${change.field}`, change.field);
                return (
                  <Row key={change.id} $choice={choice}>
                    <FieldName>{label}</FieldName>
                    <Cell style={{ color: '#64748b' }}>
                      <CellLabel><FormattedMessage id="personAdmin.changeSet.current" defaultMessage="Current" /></CellLabel>
                      {valueText(t, change.field, change.currentValue) || '—'}
                    </Cell>
                    <Cell>
                      <CellLabel><FormattedMessage id="personAdmin.changeSet.proposed" defaultMessage="Proposed" /></CellLabel>
                      <strong style={{ fontWeight: 600 }}>{valueText(t, change.field, change.proposedValue) || '—'}</strong>
                      {change.confidence != null && (
                        <HintNote> · <FormattedMessage id="personAdmin.changeSet.confidence" defaultMessage="confidence {value}" values={{ value: change.confidence }} /></HintNote>
                      )}
                      {change.canBeDecided && <Sources sources={change.sources} />}
                    </Cell>
                    <Cell>
                      {change.canBeDecided ? (
                        <>
                          <Toggles role="group" aria-label={t('personAdmin.changeSet.decisionFor', 'Decision for {field}', { field: label })}>
                            <Toggle type="button" $pressed={choice === 'ACCEPT'} aria-pressed={choice === 'ACCEPT'} disabled={busy}
                              onClick={() => decide(set.id, change.field, 'ACCEPT')}>
                              <Check size={14} aria-hidden="true" /><FormattedMessage id="personAdmin.changeSet.accept" defaultMessage="Accept" />
                            </Toggle>
                            <Toggle type="button" $reject $pressed={choice === 'REJECT'} aria-pressed={choice === 'REJECT'} disabled={busy}
                              onClick={() => decide(set.id, change.field, 'REJECT')}>
                              <X size={14} aria-hidden="true" /><FormattedMessage id="personAdmin.changeSet.reject" defaultMessage="Reject" />
                            </Toggle>
                          </Toggles>
                          <Outcome $choice={choice}>
                            {choice === 'ACCEPT' && <FormattedMessage id="personAdmin.changeSet.willAccept" defaultMessage="Will be accepted" />}
                            {choice === 'REJECT' && <FormattedMessage id="personAdmin.changeSet.willReject" defaultMessage="Will be rejected" />}
                            {!choice && <FormattedMessage id="personAdmin.changeSet.undecided" defaultMessage="Not decided yet" />}
                          </Outcome>
                        </>
                      ) : <StateLabel state={change.state} waiting={hasAccount} />}
                    </Cell>
                  </Row>
                );
              })}
            </Rows>

            {set.status === 'OPEN' && (
              <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginTop: '.75rem' }}>
                <Button type="button" disabled={busy || count === 0} onClick={() => save(set)}>
                  <Save size={15} aria-hidden="true" />
                  <FormattedMessage id="personAdmin.changeSet.save" defaultMessage="{count, plural, =0 {Save decisions} one {Save # decision} other {Save # decisions}}" values={{ count }} />
                </Button>
                <Button type="button" secondary disabled={busy} onClick={() => close(set)}>
                  <X size={15} aria-hidden="true" /><FormattedMessage id="personAdmin.changeSet.close" defaultMessage="Close without applying" />
                </Button>
              </div>
            )}
          </section>
        );
      })}
    </Wrap>
  );
};

export default ChangeSetsCard;
